import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Typography,
} from "@mui/joy";
import type { CanvasDataSource } from "../../../types";
import { HIERARCHY_DEFAULT_COLOR } from "../../../theme/canvasTokens";
import { useHierarchyBuilderData } from "../../../hooks/useHierarchyBuilderData";
import { hierarchyViewFields, HierarchyBuilderField } from "./hierarchyBuilder";
import type {
  HierarchyConfig,
  HierarchyNodeMapping,
  HierarchyRelationMapping,
} from "../elements/hierarchyGraph";
interface Props {
  props: Record<string, unknown>;
  dataSources: CanvasDataSource[];
  onChange: (props: Record<string, unknown>) => void;
}
function Choice({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Box
        component="select"
        aria-label={label}
        value={value}
        onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
          onChange(event.target.value)
        }
        sx={{
          p: 1,
          bgcolor: "background.surface",
          color: "text.primary",
          borderColor: "divider",
          borderRadius: "sm",
        }}
      >
        <option value="">Select…</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Box>
    </FormControl>
  );
}
function ColorEntry({
  label,
  values,
  onChange,
}: {
  label: string;
  values: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
}) {
  const [name, setName] = useState("");
  const [color, setColor] = useState(HIERARCHY_DEFAULT_COLOR);
  return (
    <Stack spacing={1}>
      <Typography level="body-sm">{label}</Typography>
      {Object.entries(values).map(([key, value]) => (
        <Stack key={key} direction="row" spacing={1}>
          <Typography>{key}</Typography>
          <Input
            type="color"
            aria-label={`${label} ${key}`}
            value={value}
            onChange={(event) =>
              onChange({ ...values, [key]: event.target.value })
            }
          />
          <Button
            onClick={() =>
              onChange(
                Object.fromEntries(
                  Object.entries(values).filter(([kind]) => kind !== key),
                ),
              )
            }
          >
            Remove {key} colour
          </Button>
        </Stack>
      ))}
      <Input
        aria-label={`New ${label} value`}
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <Input
        type="color"
        aria-label={`New ${label} colour`}
        value={color}
        onChange={(event) => setColor(event.target.value)}
      />
      <Button
        disabled={!name.trim()}
        onClick={() => {
          onChange({ ...values, [name.trim()]: color });
          setName("");
        }}
      >
        Add {label.toLowerCase()}
      </Button>
    </Stack>
  );
}
/** Uses admin schema/View metadata to save resolved bindings; all runtime data stays on ordinary View queries. */
export function HierarchyProperties({ props, dataSources, onChange }: Props) {
  const data = useHierarchyBuilderData(dataSources);
  const config = (props.hierarchy_config || {
    nodes: [],
    relationships: [],
    layout: "tree",
    initial_depth: 2,
    max_depth: 50,
  }) as HierarchyConfig;
  const patch = (change: Partial<HierarchyConfig>) =>
    onChange({ hierarchy_config: { ...config, ...change } });
  const viewFor = (alias: string) => data.views[alias];
  const fieldsFor = (alias: string): HierarchyBuilderField[] => {
    const view = viewFor(alias);
    return view
      ? hierarchyViewFields(view, data.tables, data.relationships)
      : [];
  };
  const newNode = (alias: string): HierarchyNodeMapping => {
    const fields = fieldsFor(alias),
      key = fields.find((field) => field.focus && field.column.is_primary_key);
    return {
      data_source_alias: alias,
      table_id: String(viewFor(alias)?.focus_table_id || ""),
      id_field: key?.name || "",
      label_field:
        fields.find(
          (field) =>
            !field.column.is_primary_key && !field.column.is_foreign_key,
        )?.name ||
        key?.name ||
        "",
    };
  };
  const patchNode = (index: number, change: Partial<HierarchyNodeMapping>) => {
    const nodes = config.nodes.map((node, position) =>
      position === index ? { ...node, ...change } : node,
    );
    const aliases = new Set(nodes.map((node) => node.data_source_alias));
    patch({
      nodes,
      relationships: config.relationships.filter(
        (relation) =>
          aliases.has(relation.from_data_source_alias) &&
          aliases.has(relation.to_data_source_alias),
      ),
    });
  };
  const options: { label: string; binding: HierarchyRelationMapping }[] = [];
  for (const from of config.nodes)
    for (const to of config.nodes)
      for (const relation of data.relationships) {
        const fk = fieldsFor(from.data_source_alias).find(
          (field) => field.focus && field.column.id === relation.from_column_id,
        );
        const pk = fieldsFor(to.data_source_alias).find(
          (field) =>
            field.focus &&
            field.column.id === relation.to_column_id &&
            field.name === to.id_field,
        );
        if (
          fk &&
          pk &&
          relation.from_table_id === from.table_id &&
          relation.to_table_id === to.table_id
        )
          options.push({
            label: `${relation.relationship_name || "Relationship"}: ${from.data_source_alias} → ${to.data_source_alias}`,
            binding: {
              relationship_ids: [relation.id],
              relationship_name: relation.relationship_name || "Relationship",
              from_data_source_alias: from.data_source_alias,
              to_data_source_alias: to.data_source_alias,
              from_field: fk.name,
              to_field: pk.name,
              reverse: false,
            },
          });
      }
  for (const link of dataSources.filter((source) => source.view_id)) {
    const tableId = String(viewFor(link.id)?.focus_table_id || "");
    const relations = data.relationships.filter(
      (relation) => relation.from_table_id === tableId,
    );
    for (const first of relations)
      for (const second of relations) {
        if (first.id === second.id) continue;
        const from = config.nodes.find(
          (node) =>
            node.table_id === first.to_table_id &&
            fieldsFor(node.data_source_alias).some(
              (field) =>
                field.name === node.id_field &&
                field.column.id === first.to_column_id,
            ),
        );
        const to = config.nodes.find(
          (node) =>
            node.table_id === second.to_table_id &&
            fieldsFor(node.data_source_alias).some(
              (field) =>
                field.name === node.id_field &&
                field.column.id === second.to_column_id,
            ),
        );
        const fromField = fieldsFor(link.id).find(
          (field) => field.focus && field.column.id === first.from_column_id,
        );
        const toField = fieldsFor(link.id).find(
          (field) => field.focus && field.column.id === second.from_column_id,
        );
        if (from && to && fromField && toField)
          options.push({
            label: `${link.name}: ${first.relationship_name || fromField.name} → ${second.relationship_name || toField.name}`,
            binding: {
              relationship_ids: [first.id, second.id],
              link_data_source_alias: link.id,
              from_data_source_alias: from.data_source_alias,
              to_data_source_alias: to.data_source_alias,
              from_field: fromField.name,
              to_field: toField.name,
              reverse: false,
            },
          });
      }
  }
  const [selectedRelation, setSelectedRelation] = useState("");
  const unused = dataSources.find(
    (source) =>
      source.view_id &&
      !config.nodes.some((node) => node.data_source_alias === source.id),
  );
  const patchRelation = (
    index: number,
    change: Partial<HierarchyRelationMapping>,
  ) =>
    patch({
      relationships: config.relationships.map((relation, position) =>
        position === index ? { ...relation, ...change } : relation,
      ),
    });
  if (data.error)
    return (
      <Alert color="danger">Hierarchy metadata could not be loaded.</Alert>
    );
  if (data.loading)
    return <Typography>Loading View and relationship metadata…</Typography>;
  return (
    <Stack spacing={1.5}>
      <Divider />
      <Typography level="title-sm">Hierarchy</Typography>
      <Typography level="body-sm">
        Read-only items and existing relationships. Layout does not change the
        relationship definition.
      </Typography>
      <Choice
        label="Layout"
        value={config.layout}
        options={[
          { value: "tree", label: "Top-down tree" },
          { value: "list", label: "Indented list" },
          { value: "flow", label: "Left-to-right flow" },
          { value: "radial", label: "Radial" },
        ]}
        onChange={(value) =>
          patch({ layout: value as HierarchyConfig["layout"] })
        }
      />
      <FormControl>
        <FormLabel>Initial depth</FormLabel>
        <Input
          aria-label="Initial depth"
          type="number"
          value={config.initial_depth ?? 2}
          onChange={(event) =>
            patch({ initial_depth: Number(event.target.value) })
          }
        />
      </FormControl>
      <FormControl>
        <FormLabel>Maximum depth (1–50)</FormLabel>
        <Input
          aria-label="Maximum depth"
          type="number"
          value={config.max_depth ?? 50}
          onChange={(event) => patch({ max_depth: Number(event.target.value) })}
        />
      </FormControl>
      {config.nodes.map((node, index) => {
        const fields = fieldsFor(node.data_source_alias);
        const fieldsOptions = fields.map((field) => ({
          value: field.name,
          label: `${field.label} (${field.name})`,
        }));
        return (
          <Stack key={index} spacing={1}>
            <Typography level="title-sm">Item View {index + 1}</Typography>
            <Choice
              label={`Item ${index + 1} View`}
              value={node.data_source_alias}
              options={dataSources
                .filter(
                  (source) =>
                    source.view_id &&
                    (source.id === node.data_source_alias ||
                      !config.nodes.some(
                        (mapping) => mapping.data_source_alias === source.id,
                      )),
                )
                .map((source) => ({ value: source.id, label: source.name }))}
              onChange={(alias) => patchNode(index, newNode(alias))}
            />
            <Choice
              label={`Item ${index + 1} key`}
              value={node.id_field}
              options={fields
                .filter((field) => field.focus && field.column.is_primary_key)
                .map((field) => ({ value: field.name, label: field.name }))}
              onChange={(value) => patchNode(index, { id_field: value })}
            />
            {!node.id_field && (
              <Alert>
                Project this View's focus primary key before saving.
              </Alert>
            )}
            {(["label", "subtitle", "avatar", "icon", "status"] as const).map(
              (name) => (
                <Choice
                  key={name}
                  label={`Item ${index + 1} ${name}`}
                  value={node[`${name}_field`] || ""}
                  options={fieldsOptions}
                  onChange={(value) =>
                    patchNode(index, { [`${name}_field`]: value || null })
                  }
                />
              ),
            )}
            <FormControl>
              <FormLabel>Detail canvas ID (optional)</FormLabel>
              <Input
                aria-label={`Item ${index + 1} detail canvas`}
                value={String(
                  node.node_click_action?.config?.target_canvas_id || "",
                )}
                onChange={(event) =>
                  patchNode(index, {
                    node_click_action: event.target.value
                      ? {
                          type: "NAVIGATE",
                          config: {
                            target_canvas_id: event.target.value,
                            record_source: "selected",
                            return_to_source: true,
                          },
                        }
                      : null,
                  })
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>Starting keys (optional, comma separated)</FormLabel>
              <Input
                key={node.data_source_alias}
                aria-label={`Item ${index + 1} starting keys`}
                defaultValue={(
                  config.starting_items?.[node.data_source_alias] || []
                ).join(", ")}
                onBlur={(event) =>
                  patch({
                    starting_items: {
                      ...config.starting_items,
                      [node.data_source_alias]: event.target.value
                        .split(",")
                        .map((value) => value.trim())
                        .filter(Boolean),
                    },
                  })
                }
              />
            </FormControl>
            <ColorEntry
              label={`Item ${index + 1} status colours`}
              values={node.status_colors || {}}
              onChange={(value) => patchNode(index, { status_colors: value })}
            />
            <Button
              onClick={() => {
                const nodes = config.nodes.filter(
                  (_, position) => position !== index,
                );
                const aliases = new Set(
                  nodes.map((mapping) => mapping.data_source_alias),
                );
                patch({
                  nodes,
                  relationships: config.relationships.filter(
                    (relation) =>
                      aliases.has(relation.from_data_source_alias) &&
                      aliases.has(relation.to_data_source_alias),
                  ),
                  starting_items: Object.fromEntries(
                    Object.entries(config.starting_items || {}).filter(
                      ([alias]) => aliases.has(alias),
                    ),
                  ),
                });
              }}
            >
              Remove item {index + 1}
            </Button>
          </Stack>
        );
      })}
      <Button
        disabled={!unused}
        onClick={() => {
          if (unused) patch({ nodes: [...config.nodes, newNode(unused.id)] });
        }}
      >
        Add item View
      </Button>
      <Choice
        label="Existing relationship"
        value={selectedRelation}
        options={options.map((option, index) => ({
          value: String(index),
          label: option.label,
        }))}
        onChange={setSelectedRelation}
      />
      <Button
        disabled={!options[Number(selectedRelation)] || selectedRelation === ""}
        onClick={() =>
          patch({
            relationships: [
              ...config.relationships,
              options[Number(selectedRelation)].binding,
            ],
          })
        }
      >
        Add relationship
      </Button>
      {config.relationships.map((relation, index) => (
        <Stack key={index} spacing={1}>
          <Typography>
            {relation.relationship_name ||
              `${relation.from_data_source_alias} → ${relation.to_data_source_alias}`}
          </Typography>
          <Checkbox
            label={`Reverse relationship ${index + 1}`}
            checked={relation.reverse === true}
            onChange={(event) =>
              patchRelation(index, { reverse: event.target.checked })
            }
          />
          {relation.link_data_source_alias &&
            (["type", "label"] as const).map((name) => (
              <Choice
                key={name}
                label={`Relationship ${index + 1} ${name} field`}
                value={relation[`${name}_field`] || ""}
                options={fieldsFor(relation.link_data_source_alias!).map(
                  (field) => ({ value: field.name, label: field.label }),
                )}
                onChange={(value) =>
                  patchRelation(index, { [`${name}_field`]: value || null })
                }
              />
            ))}
          <Button
            onClick={() =>
              patch({
                relationships: config.relationships.filter(
                  (_, position) => position !== index,
                ),
              })
            }
          >
            Remove relationship {index + 1}
          </Button>
        </Stack>
      ))}
      <ColorEntry
        label="Relationship kinds"
        values={Object.fromEntries(
          Object.entries(config.relationship_styles || {}).map(
            ([kind, style]) => [kind, style.color],
          ),
        )}
        onChange={(values) =>
          patch({
            relationship_styles: Object.fromEntries(
              Object.entries(values).map(([kind, color]) => [
                kind,
                {
                  color,
                  dashed: config.relationship_styles?.[kind]?.dashed || false,
                },
              ]),
            ),
          })
        }
      />
      {Object.entries(config.relationship_styles || {}).map(([kind, style]) => (
        <Checkbox
          key={kind}
          label={`Dashed ${kind}`}
          checked={style.dashed === true}
          onChange={(event) =>
            patch({
              relationship_styles: {
                ...config.relationship_styles,
                [kind]: { ...style, dashed: event.target.checked },
              },
            })
          }
        />
      ))}
    </Stack>
  );
}
